import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { oauthLink, openOAuthLink } from '../lib/oauth-link'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/mail-oauth',
  component: MailOAuthSettings,
})

function MailOAuthSettings() {
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => api.getAppSettings(),
    enabled: !!meData?.user,
  })

  const [clientId, setClientId] = useState('')
  const [tenant, setTenant] = useState('common')
  const [clientSecret, setClientSecret] = useState('')

  useEffect(() => {
    if (!data) return
    setClientId(data.settings.oauthMicrosoftClientId ?? '')
    setTenant(data.settings.oauthMicrosoftTenant ?? 'common')
  }, [data])

  // Read admin-consent callback flags + strip them from the URL so a
  // reload doesn't re-fire the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ok = params.get('consent_ok')
    const err = params.get('consent_error')
    if (ok) notify.success('Admin-Consent erteilt — User können sich jetzt anmelden')
    if (err) notify.error(`Admin-Consent fehlgeschlagen: ${err}`)
    if (ok || err) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const save = useMutation({
    mutationFn: () => {
      // Empty secret on save = keep existing. Only send when user typed.
      const patch: Parameters<typeof api.updateAppSettings>[0] = {
        oauthMicrosoftClientId: clientId.trim() || null,
        oauthMicrosoftTenant: tenant.trim() || 'common',
      }
      if (clientSecret.length > 0) {
        patch.oauthMicrosoftClientSecret = clientSecret
      }
      return api.updateAppSettings(patch)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['app-settings'] })
      setClientSecret('')
      notify.success('Microsoft OAuth gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') return <Navigate to="/" />

  const redirectUri = oauthLink(window.location.origin, '/api/mail/oauth/microsoft/callback').url

  return (
    <SettingsLayout
      title="Mail · Microsoft OAuth"
      description="Azure-AD-App registrieren und Credentials für IMAP/SMTP-OAuth eintragen"
    >
      <div className="space-y-6 max-w-2xl">
        <section className="rounded-lg border border-border-subtle bg-bg-overlay/30 p-4 space-y-3">
          <h2 className="font-medium flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-accent" />
            Azure-AD-App registrieren
          </h2>
          <ol className="text-sm text-text-secondary space-y-1.5 list-decimal pl-5">
            <li>
              <a
                className="text-accent hover:underline"
                href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                target="_blank"
                rel="noreferrer"
              >
                Azure-Portal → App registrations
              </a>{' '}
              → <em>New registration</em>
            </li>
            <li>
              Name z.B. „Ember Mail“, Supported account types: <em>Multitenant</em>
            </li>
            <li>
              Redirect URI:{' '}
              <code className="text-xs bg-bg-overlay/60 px-1 py-0.5 rounded">{redirectUri}</code>
            </li>
            <li>
              <strong>API permissions → Add → Microsoft Graph → Delegated</strong>:{' '}
              <code className="text-xs">offline_access</code>,{' '}
              <code className="text-xs">openid</code>, <code className="text-xs">profile</code>,{' '}
              <code className="text-xs">email</code>
            </li>
            <li>
              <strong>
                API permissions → Add → APIs my organization uses → „Office 365 Exchange Online“ →
                Delegated
              </strong>
              : <code className="text-xs">IMAP.AccessAsUser.All</code>,{' '}
              <code className="text-xs">SMTP.Send</code>
              <p className="text-[11px] text-text-tertiary mt-1">
                Wichtig: IMAP+SMTP liegen unter Exchange Online, NICHT unter Microsoft Graph.
              </p>
            </li>
            <li>Certificates & secrets → New client secret → kopieren</li>
            <li>Application (client) ID + Secret unten eintragen + speichern</li>
            <li>
              Danach unten den „Admin-Consent erteilen“-Button klicken — das gibt einmalig die
              Exchange-Permissions für alle User im Tenant frei.
            </li>
          </ol>
        </section>

        {isLoading ? (
          <p className="text-sm text-text-tertiary">Lade …</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="m-client-id">Client ID (Application ID)</Label>
              <Input
                id="m-client-id"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="m-tenant">Tenant</Label>
              <Input
                id="m-tenant"
                value={tenant}
                onChange={(e) => setTenant(e.target.value)}
                placeholder="common"
              />
              <p className="text-[11px] text-text-tertiary">
                <code>common</code> = jede Microsoft-Identität (Multitenant). Für single-tenant hier
                die Tenant-ID eintragen.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="m-secret">Client Secret</Label>
              <Input
                id="m-secret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={
                  data?.settings.oauthMicrosoftSecretSet
                    ? '••••••••  (gespeichert — leer lassen zum Beibehalten)'
                    : 'Neues Secret eintragen'
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? '…' : 'Speichern'}
              </Button>
              {data?.settings.oauthMicrosoftSecretSet && (
                <span className="text-xs text-state-success">✓ Secret gespeichert</span>
              )}
            </div>
          </div>
        )}

        {data?.settings.oauthMicrosoftClientId && data?.settings.oauthMicrosoftSecretSet && (
          <section className="rounded-lg border border-border-subtle bg-bg-overlay/30 p-4 space-y-3">
            <h2 className="font-medium">Admin-Consent</h2>
            <p className="text-sm text-text-secondary">
              Die Exchange-Scopes (<code className="text-xs">IMAP.AccessAsUser.All</code>,{' '}
              <code className="text-xs">SMTP.Send</code>) sind admin-consent-pflichtig. Klick den
              Button unten einmal — du wirst zu Microsoft umgeleitet, bestätigst die Permissions für
              deinen gesamten Tenant, und landest wieder hier. Danach können User via „Mit Microsoft
              anmelden" ihren Account verbinden, ohne die admin-required-Hürde zu sehen.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                openOAuthLink(api.mailOAuthAdminConsentUrl())
              }}
            >
              Admin-Consent erteilen
            </Button>
          </section>
        )}
      </div>
    </SettingsLayout>
  )
}
